import React, { useState, useEffect } from 'react';
import './PasswordReset.css';

interface PasswordResetConfirmProps {
  token: string;
  onSuccess: () => void;
}

const PasswordResetConfirm: React.FC<PasswordResetConfirmProps> = ({ token, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Verify token on mount
  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    setIsVerifying(true);
    try {
      const basePath = '/databaseOps/';
      const response = await fetch(`${basePath}verifyResetToken.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (data.valid) {
        setTokenValid(true);
        setUserEmail(data.email);
      } else {
        setError(data.error || 'Ungültiger oder abgelaufener Link');
        setTokenValid(false);
      }
    } catch (error) {
      console.error('Token verification error:', error);
      setError('Fehler bei der Überprüfung des Links');
      setTokenValid(false);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    if (password !== passwordConfirm) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    setIsLoading(true);

    try {
      const basePath = '/databaseOps/';
      const response = await fetch(`${basePath}resetPassword.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Zurücksetzen fehlgeschlagen');
        setIsLoading(false);
        return;
      }

      // Success - notify parent
      onSuccess();

    } catch (error: any) {
      console.error('Password reset error:', error);
      setError('Netzwerkfehler. Bitte versuchen Sie es erneut.');
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="password-reset-container">
        <div className="password-reset-box">
          <div className="password-reset-loading">
            <p>Link wird überprüft...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="password-reset-container">
        <div className="password-reset-box">
          <div className="password-reset-error-page">
            <h3>Ungültiger Link</h3>
            <p>{error}</p>
            <p>
              Der Link zum Zurücksetzen des Passworts ist ungültig oder abgelaufen.
              Links sind nur 15 Minuten gültig.
            </p>
            <button onClick={() => window.location.hash = '#/login'} className="btn-primary">
              Zur Anmeldung
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-reset-container">
      <div className="password-reset-box">
        <div className="password-reset-header">
          <h2>Neues Passwort erstellen</h2>
          {userEmail && (
            <p className="password-reset-email">Für: {userEmail}</p>
          )}
        </div>

        {error && (
          <div className="password-reset-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">Neues Passwort</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              minLength={8}
              placeholder="Mindestens 8 Zeichen"
              autoFocus
            />
            <small className="form-help">Mindestens 8 Zeichen</small>
          </div>

          <div className="form-group">
            <label htmlFor="passwordConfirm">Passwort bestätigen</label>
            <input
              id="passwordConfirm"
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              disabled={isLoading}
              minLength={8}
              placeholder="Passwort wiederholen"
            />
          </div>

          <button type="submit" disabled={isLoading} className="btn-primary btn-full">
            {isLoading ? 'Wird gespeichert...' : 'Passwort ändern'}
          </button>
        </form>

        <div className="password-reset-info">
          <p>Nach dem Zurücksetzen können Sie sich mit Ihrem neuen Passwort anmelden.</p>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetConfirm;
